"use client";

import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  TABLE,
  TD,
  TH,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { EnigmaStrings } from "@/components/tools/enigma-machine";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type {
  EnigmaPress,
  ReflectorName,
  RotorName,
  SignalPath,
  Triple,
} from "@/lib/tool-kit/ciphers/enigma";

/** The Enigma I keyboard and lamp board: QWERTZ, with P starting the bottom row. */
export const ENIGMA_ROWS = ["QWERTZUIO", "ASDFGHJK", "PYXCVBNML"] as const;

const LOG_LIMIT = 12;

/** Left, middle, right: the order a key sheet lists them in. */
export function slotNames(strings: EnigmaStrings): Triple<string> {
  return [strings.slotLeft, strings.slotMiddle, strings.slotRight];
}

export function RotorWindows({
  rotors,
  window,
  strings,
}: {
  rotors: Triple<RotorName>;
  window: string;
  strings: EnigmaStrings;
}) {
  const slots = slotNames(strings);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.windowsHeading}</h3>
      <div className="flex gap-3">
        {rotors.map((rotor, index) => (
          <div className="text-center" key={slots[index]}>
            <p className="text-muted-foreground text-xs">{slots[index]}</p>
            <p className="mx-auto mt-1 flex h-12 w-10 items-center justify-center rounded-md border-2 border-foreground/70 bg-card font-mono font-semibold text-2xl text-foreground">
              <span className="sr-only">
                {t(strings.windowLabel, {
                  slot: slots[index],
                  letter: window.charAt(index),
                })}
              </span>
              <span aria-hidden="true">{window.charAt(index)}</span>
            </p>
            <p className="mt-1 font-mono text-muted-foreground text-xs">
              {rotor}
            </p>
          </div>
        ))}
      </div>
      <p className={HINT}>{strings.windowsHint}</p>
    </div>
  );
}

export function Lampboard({
  lit,
  strings,
}: {
  lit: string | null;
  strings: EnigmaStrings;
}) {
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.lampboard}</h3>
      <div
        aria-label={
          lit ? t(strings.lampLit, { letter: lit }) : strings.lampsDark
        }
        className="space-y-1.5 rounded-lg bg-neutral-900 p-3"
        role="img"
      >
        {ENIGMA_ROWS.map((row) => (
          <div className="flex justify-center gap-1.5" key={row}>
            {[...row].map((letter) => (
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full border font-mono font-semibold text-sm transition-colors ${
                  letter === lit
                    ? "border-amber-200 bg-amber-300 text-neutral-900 shadow-[0_0_12px_rgba(252,211,77,0.9)]"
                    : "border-neutral-600 bg-neutral-800 text-neutral-400"
                }`}
                key={letter}
              >
                {letter}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Keyboard({
  onPress,
  onDelete,
  onClear,
  canDelete,
  strings,
}: {
  onPress: (letter: string) => void;
  onDelete: () => void;
  onClear: () => void;
  canDelete: boolean;
  strings: EnigmaStrings;
}) {
  return (
    <div className="space-y-2">
      <p className={LABEL}>{strings.keyboard}</p>
      <div className="space-y-1.5">
        {ENIGMA_ROWS.map((row) => (
          <div className="flex justify-center gap-1.5" key={row}>
            {[...row].map((letter) => (
              <button
                aria-label={t(strings.pressKey, { letter })}
                className="h-9 w-9 rounded-full border border-border bg-card font-mono font-semibold text-foreground text-sm shadow-sm transition-colors hover:bg-muted active:translate-y-px"
                key={letter}
                onClick={() => onPress(letter)}
                type="button"
              >
                {letter}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-3 pt-1">
        <button
          className={BUTTON_SECONDARY}
          disabled={!canDelete}
          onClick={onDelete}
          type="button"
        >
          {strings.deleteLast}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={!canDelete}
          onClick={onClear}
          type="button"
        >
          {strings.clearMessage}
        </button>
      </div>
    </div>
  );
}

/** The ten letters of `SignalPath.path`, each with the stage it leaves. */
export function SignalRoute({
  path,
  rotors,
  reflector,
  strings,
}: {
  path: SignalPath;
  rotors: Triple<RotorName>;
  reflector: ReflectorName;
  strings: EnigmaStrings;
}) {
  const slots = slotNames(strings);
  const rotor = (index: number) =>
    t(strings.stageRotor, { rotor: rotors[index], slot: slots[index] });
  const stages = [
    strings.stageKey,
    strings.stagePlugboard,
    rotor(2),
    rotor(1),
    rotor(0),
    t(strings.stageReflector, { name: reflector }),
    rotor(0),
    rotor(1),
    rotor(2),
    strings.stageLamp,
  ];
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.pathHeading}</h3>
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-2 text-sm">
        {path.path.map((letter, index) => (
          <li
            className="flex items-center gap-1"
            // biome-ignore lint/suspicious/noArrayIndexKey: stages are positional
            key={index}
          >
            {index > 0 ? (
              <span aria-hidden="true" className="text-muted-foreground">
                →
              </span>
            ) : null}
            <span className="rounded-md border border-border bg-card px-2 py-1">
              <span className="block text-[10px] text-muted-foreground leading-tight">
                {stages[index]}
              </span>
              <span className="font-mono font-semibold text-foreground">
                {letter}
              </span>
            </span>
          </li>
        ))}
      </ol>
      <p className={HINT}>{strings.pathHint}</p>
    </div>
  );
}

export function PressLog({
  presses,
  strings,
}: {
  presses: readonly EnigmaPress[];
  strings: EnigmaStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const first = Math.max(0, presses.length - LOG_LIMIT);
  const shown = presses.slice(first);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.logHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.logIndex}
              </th>
              <th className={TH} scope="col">
                {strings.logWindows}
              </th>
              <th className={TH} scope="col">
                {strings.logKey}
              </th>
              <th className={TH} scope="col">
                {strings.logLamp}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((press, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: key presses are positions in the message
              <tr key={first + index}>
                <td className={`${TD} text-muted-foreground`}>
                  {fmt(first + index + 1)}
                </td>
                <td className={`${TD} font-mono`}>{press.window}</td>
                <td className={`${TD} font-mono`}>{press.input}</td>
                <td className={`${TD} font-mono font-semibold`}>
                  {press.output}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {presses.length > LOG_LIMIT ? (
        <p className={HINT}>
          {tPlural(strings.logLimit, locale, LOG_LIMIT, {
            count: fmt(LOG_LIMIT),
          })}
        </p>
      ) : null}
    </div>
  );
}
